import { capitalize } from 'lodash';
import { useMemo, useState } from 'react';

import { Grid, GridItem, Tabs, TabsList, TabsTrigger, Text } from '@/design-system/components/ui';
import { breakpoints, useMediaQuery } from '@/design-system/hooks/useMediaQuery';

import { EventTriggerType } from '../../domain';
import { usePendingTriggersStore } from '../../stores/pendingTriggersStore';
import { TriggerTypeSearchBar } from '../TriggerTypeSearchBar';
import {
  DEFAULT_TRIGGER_TYPES,
  SelectTriggerOption,
  TriggerCategories,
  triggerOptions,
} from '../TriggerTypeSelectorModal';
import { CryptoTriggersToggle } from './CryptoTriggersToggle';
import { TriggerTypeSelectorCard } from './TriggerTypeSelectorCard';
import { TriggerTypeSelectorListItem } from './TriggerTypeSelectorListItem';
import { TriggerTypeViewToggle } from './TriggerTypeViewToggle';

interface Props {
  onSelectTrigger: (option: SelectTriggerOption) => void;
}

interface TriggerOptionsGridProps {
  options: SelectTriggerOption[];
  viewMode: 'list' | 'card';
  emptyMessage: string;
  getSelectedCount: (option: SelectTriggerOption) => number;
  onSelectTrigger: (option: SelectTriggerOption) => void;
}

const TriggerOptionsGrid = ({
  options,
  viewMode,
  emptyMessage,
  getSelectedCount,
  onSelectTrigger,
}: TriggerOptionsGridProps) => {
  if (options.length === 0) {
    return (
      <Text variant="body-sm" color="muted" className="tw:text-center tw:py-8">
        {emptyMessage}
      </Text>
    );
  }

  if (viewMode === 'card') {
    return (
      <Grid cols={12} gap={4} className="tw:items-stretch">
        {options.map((option) => (
          <GridItem key={option.title} span={12} sm={6} md={4} className="tw:flex">
            <TriggerTypeSelectorCard
              option={option}
              selectedCount={getSelectedCount(option)}
              onClick={() => onSelectTrigger(option)}
            />
          </GridItem>
        ))}
      </Grid>
    );
  }

  return (
    <div className="tw:flex tw:flex-col tw:gap-4">
      {options.map((option) => (
        <TriggerTypeSelectorListItem
          key={option.title}
          option={option}
          selectedCount={getSelectedCount(option)}
          onClick={() => onSelectTrigger(option)}
        />
      ))}
    </div>
  );
};

export const TriggerTypeSelector = ({ onSelectTrigger }: Props) => {
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.sm})`);

  const [selectedTab, setSelectedTab] = useState(TriggerCategories.ALL);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'list' | 'card'>('card');
  const [showCrypto, setShowCrypto] = useState(false);

  const pendingTriggers = usePendingTriggersStore((s) => s.pendingTriggers);

  // Force list view on mobile
  const effectiveViewMode = isMobile ? 'list' : viewMode;

  const getSelectedCount = (option: SelectTriggerOption): number => {
    return pendingTriggers.filter((t) => t.triggerType === option.triggerType).length;
  };

  const { defaultOptions, cryptoOptions } = useMemo(() => {
    const hasStripePaymentPending = pendingTriggers.some((t) => t.triggerType === EventTriggerType.StripePayment);

    const matchesSearch = (option: SelectTriggerOption) =>
      option.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      option.description.toLowerCase().includes(searchQuery.toLowerCase());

    const isAvailable = (option: SelectTriggerOption) =>
      !(option.triggerType === EventTriggerType.StripePayment && hasStripePaymentPending);

    const searchable = triggerOptions.filter((option) => matchesSearch(option) && isAvailable(option));

    return {
      defaultOptions: searchable.filter((option) => DEFAULT_TRIGGER_TYPES.includes(option.triggerType)),
      cryptoOptions: searchable.filter(
        (option) =>
          !DEFAULT_TRIGGER_TYPES.includes(option.triggerType) &&
          (selectedTab === TriggerCategories.ALL || option.category === selectedTab),
      ),
    };
  }, [selectedTab, searchQuery, pendingTriggers]);

  const hiddenSearchMatchCount = useMemo(() => {
    if (showCrypto || !searchQuery.trim()) return 0;

    return triggerOptions.filter(
      (option) =>
        !DEFAULT_TRIGGER_TYPES.includes(option.triggerType) &&
        (option.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          option.description.toLowerCase().includes(searchQuery.toLowerCase())),
    ).length;
  }, [showCrypto, searchQuery]);

  const handleShowCryptoChange = (value: boolean) => {
    setShowCrypto(value);
    setSelectedTab(TriggerCategories.ALL);
  };

  return (
    <div className="tw:flex tw:flex-col tw:gap-6">
      <div className="tw:flex tw:flex-row tw:justify-between tw:items-center tw:gap-4">
        <div className="tw:flex-1">
          <TriggerTypeSearchBar value={searchQuery} onChange={setSearchQuery} />
        </div>
        {!isMobile && <TriggerTypeViewToggle viewMode={viewMode} onChange={setViewMode} />}
      </div>

      <TriggerOptionsGrid
        options={defaultOptions}
        viewMode={effectiveViewMode}
        emptyMessage="No triggers found"
        getSelectedCount={getSelectedCount}
        onSelectTrigger={onSelectTrigger}
      />

      {hiddenSearchMatchCount > 0 && (
        <Text variant="body-sm" color="muted">
          {hiddenSearchMatchCount === 1
            ? '1 crypto & quest trigger also matches your search. Turn on the switch below to see it.'
            : `${hiddenSearchMatchCount} crypto & quest triggers also match your search. Turn on the switch below to see them.`}
        </Text>
      )}

      <CryptoTriggersToggle checked={showCrypto} onCheckedChange={handleShowCryptoChange} />

      {showCrypto && (
        <>
          <Tabs
            value={selectedTab}
            onValueChange={(v) => setSelectedTab(v as TriggerCategories)}
            className="tw:overflow-hidden"
          >
            <TabsList className="tw:border-b tw:border-border tw:w-full tw:overflow-x-auto tw:scrollbar-none">
              {Object.values(TriggerCategories).map((category) => (
                <TabsTrigger key={category} value={category}>
                  {capitalize(category)}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>

          <TriggerOptionsGrid
            options={cryptoOptions}
            viewMode={effectiveViewMode}
            emptyMessage="No triggers found"
            getSelectedCount={getSelectedCount}
            onSelectTrigger={onSelectTrigger}
          />
        </>
      )}
    </div>
  );
};
